import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { isNativeApp } from '@/utils/platformUtils';
import { openExternal } from '@/utils/openExternal';
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate';
import { LANGUAGE_REGEX_GROUP } from '@/types/language';

/**
 * When running inside Capacitor, redirects public/marketing routes
 * to /dashboard so the user never sees the website homepage.
 * Auth routes (/sign-in, /forgot-password, /reset-password) are allowed.
 */
const allowedPublicPaths = ['/sign-in', '/sign-up', '/forgot-password', '/reset-password', '/verify-email'];

const NativeRouteGuard = () => {
  const location = useLocation();
  const navigate = useLocalizedNavigate();
  const lastHandledRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isNativeApp()) return;

    // Strip language prefix
    const stripped = location.pathname.replace(new RegExp(`^/(${LANGUAGE_REGEX_GROUP})`), '') || '/';

    // Check if this is a backoffice / admin route (allowed)
    const isProtectedRoute =
      stripped.startsWith('/dashboard') ||
      stripped.startsWith('/investment-accounts') ||
      stripped.startsWith('/add-account') ||
      stripped.startsWith('/profile') ||
      stripped.startsWith('/my-community') ||
      stripped.startsWith('/my-profits');

    const isAllowedPublic = allowedPublicPaths.some((p) => stripped.startsWith(p));

    if (!isProtectedRoute && !isAllowedPublic) {
      // Guard against repeated triggers for the same path
      if (lastHandledRef.current === location.pathname) return;
      lastHandledRef.current = location.pathname;

      const fullUrl = `${window.location.origin}${location.pathname}${location.search}${location.hash}`;
      openExternal(fullUrl).then(() => {
        navigate('/dashboard', { replace: true });
      });
    }
  }, [location.pathname, navigate]);

  return null;
};

export default NativeRouteGuard;
